import { trpc } from '@client/utils/trpc';
import { Alert, Card, CardContent, CardProps, Skeleton, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import prettyBytes from 'pretty-bytes';

export type MemoryChartCardProps = CardProps & {
  chain: string | null;
  showOffline?: boolean;
};

const STEP = 4 * 1000 * 1000 * 1000; // 4 GB

export const MemoryChartCard = ({ chain, showOffline, ...rest }: MemoryChartCardProps) => {
  const {
    data = [],
    status,
    error,
  } = trpc.chart.memory.useQuery(
    { chain, showOffline },
    {
      select: (rawData) => {
        if (rawData.length === 0) return [];

        const maxMemory = Math.max(...rawData.map((item) => item.memory));
        const bucketCount = Math.ceil(maxMemory / STEP);

        const buckets = Array.from({ length: bucketCount }, (_, index) => ({
          lowerBound: index * STEP,
          upperBound: (index + 1) * STEP,
          count: 0,
        }));

        for (const item of rawData) {
          const bucketIndex = Math.min(Math.floor(item.memory / STEP), bucketCount - 1);

          buckets[bucketIndex].count += item.count;
        }

        return buckets;
      },
    },
  );

  return (
    <Card {...rest}>
      <CardContent>
        <Typography variant="h5">{'Memory'}</Typography>

        {status === 'success' ? (
          <BarChart
            height={256}
            hideLegend
            xAxis={[
              {
                scaleType: 'band',
                data: data.map((bucket) => bucket.lowerBound),
                valueFormatter: (value: number) => `${prettyBytes(value)}`,
              },
            ]}
            yAxis={[
              {
                min: 0,
              },
            ]}
            series={[
              {
                data: data.map((bucket) => bucket.count),
              },
            ]}
          />
        ) : status === 'error' ? (
          <Alert severity="error">{error.message}</Alert>
        ) : (
          <Skeleton variant="rounded" height={256} />
        )}
      </CardContent>
    </Card>
  );
};
